import { AngebotsId } from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { GegenstandCategory } from '@ps20/bff-models';
import { IAppState } from '../localStore';
import { Vorversicherung } from '../model/vorversicherung';
import { ALLIANZ_ANSTALTSNUMMERN, Vorversicherer } from '../data/vorversicherung';
import { GegeCheckoutProdukt, Produkt } from '../model';
import { getProdukteAsAngebotsIds } from './produkt.selector';
import { orderProductList } from './checkoutProduktReihenfolge';
import { arraysShallowEqual } from './selector.util';


export const versicherungData = (state: IAppState): IAppState['versicherungData'] =>
  state.versicherungData;

export type AvailableVorversicherungenReturnType = ReturnType<typeof availableVorversicherungen>;
export type VersicherungDataReturnType = ReturnType<typeof versicherungData>;

const computeAvailableVorversicherungen = (state: IAppState): Array<Vorversicherung> => {
  const produktOrder: Array<AngebotsId> = getProdukteAsAngebotsIds(state);

  return state.vorversicherungen
    .filter(vorversicherung => vorversicherung != null)
    .filter(
      vorversicherung =>
        hasNoVorvertrag(state.produkte, vorversicherung) ||
        hasGefahrenFromVorvertrag(state.produkte, vorversicherung),
    )
    .filter(vorversicherung => isVorversicherungVisible(state.produkte, vorversicherung))
    .sort((vv1, vv2) => {
      const vv1Index = orderProductList.indexOf(vv1.appId);
      const vv2Index = orderProductList.indexOf(vv2.appId);
      if (vv1Index === vv2Index) {
        return (
          getIndexForVorversicherung(vv1, produktOrder) -
          getIndexForVorversicherung(vv2, produktOrder)
        );
      }
      return vv1Index - vv2Index;
    });
};

let cachedVorversicherungenInput: Array<Vorversicherung> | null = null;
let cachedProdukteInput: Array<unknown> | null = null;
let cachedAvailableVorversicherungenResult: Array<Vorversicherung> | null = null;

export const availableVorversicherungen = (state: IAppState): Array<Vorversicherung> => {
  if (
    cachedAvailableVorversicherungenResult !== null &&
    arraysShallowEqual(cachedVorversicherungenInput, state.vorversicherungen) &&
    arraysShallowEqual(cachedProdukteInput, state.produkte)
  ) {
    return cachedAvailableVorversicherungenResult;
  }
  cachedVorversicherungenInput = state.vorversicherungen;
  cachedProdukteInput = state.produkte;
  cachedAvailableVorversicherungenResult = computeAvailableVorversicherungen(state);
  return cachedAvailableVorversicherungenResult;
};

function isVorversicherungVisible(produkte: Array<Produkt>, vorversicherung: Vorversicherung): boolean {
  if (vorversicherung.appId !== App.GEGE) {
    return true;
  }
  const produkt = produkte.find(
    p => p.appId === vorversicherung.appId && p.angebotsId === vorversicherung.angebotsId,
  ) as GegeCheckoutProdukt;
  return produkt?.gegenstaende?.some(g => g.kategorie.id === GegenstandCategory.FAHRRAD) ?? false;
}

function hasNoVorvertrag(produkte: Array<Produkt>, vorversicherung: Vorversicherung): boolean {
  const produkt: Produkt = produkte.find(
    vorversicherungProdukt =>
      vorversicherungProdukt.appId === vorversicherung.appId &&
      vorversicherungProdukt.angebotsId === vorversicherung.angebotsId,
  );
  return !(produkt && produkt.vorvertragsnummer != null);
}

function isAppMitGefahrenFromErsatz(appId: App): boolean {
  return appId === App.VWG_V2 || appId === App.VHV_V2;
}

export function hasGefahrenFromVorvertrag(
  produkte: Array<Produkt>,
  vorversicherung: Vorversicherung,
): boolean {
  return produkte.some(
    vorversicherungProdukt =>
      vorversicherung.gefahrenFromErsatz &&
      isAppMitGefahrenFromErsatz(vorversicherungProdukt.appId) &&
      vorversicherungProdukt.angebotsId === vorversicherung.angebotsId,
  );
}

export function hasGefahrenInVorversicherung(
  produkt: Produkt,
  vorversicherungen: Array<Vorversicherung>,
): boolean {
  return vorversicherungen?.some(
    vorversicherung =>
      vorversicherung.gefahrenFromErsatz &&
      isAppMitGefahrenFromErsatz(vorversicherung.appId) &&
      vorversicherung.angebotsId === produkt.angebotsId,
  );
}

export function isVorversicherungVorhanden(
  produkt: Produkt,
  vorversicherungen: Array<Vorversicherung>,
): boolean {
  return vorversicherungen.some(
    vorversicherung =>
      vorversicherung.vorhanden &&
      isAppMitGefahrenFromErsatz(vorversicherung.appId) &&
      vorversicherung.angebotsId === produkt.angebotsId,
  );
}

function getIndexForVorversicherung(
  element: Vorversicherung,
  produktOrder: Array<AngebotsId>,
): number {
  return produktOrder.findIndex(
    angebot => angebot.appId === element.appId && angebot.angebotsId === element.angebotsId,
  );
}

export function isVersicherungsgesellschaftStale(
  value: string | null | undefined,
  availableList: Array<Vorversicherer> | null | undefined,
): boolean {
  if (!value) {
    return false;
  }
  if (!availableList || availableList.length === 0) {
    return false;
  }
  return !availableList.some(entry => entry.value === value);
}

export function hasStaleVersicherungsgesellschaft(state: IAppState): boolean {
  const list = state.versicherungData;
  const vorversicherungen = state.vorversicherungen ?? [];
  return vorversicherungen.some(
    v =>
      v?.vorhanden === true &&
      (v.vorversicherungsListe ?? []).some(item =>
        isVersicherungsgesellschaftStale(item?.versicherungsgesellschaft, list),
      ),
  );
}

export function isAllianzVorversicherer(versicherungsgesellschaft: string | null | undefined): boolean {
  return !!versicherungsgesellschaft && ALLIANZ_ANSTALTSNUMMERN.includes(versicherungsgesellschaft);
}

export type AllianzVsnrValidationError = 'format' | 'vorvertrag';

export function getAllianzVsnrValidationError(
  vsnr: string | null | undefined,
  versicherungsgesellschaft: string | null | undefined,
  vorvertragsnummern: Array<string> | null | undefined,
  allianzVsnrPattern: string | null | undefined,
): AllianzVsnrValidationError | null {
  if (!vsnr || !isAllianzVorversicherer(versicherungsgesellschaft)) {
    return null;
  }
  const normalizedVsnr = vsnr.trim().toUpperCase();
  const normalizedVorvertragsnummern = (vorvertragsnummern ?? []).map(nummer => nummer.trim().toUpperCase());
  if (normalizedVorvertragsnummern.includes(normalizedVsnr)) {
    return 'vorvertrag';
  }
  if (!allianzVsnrPattern) {
    return null;
  }
  return new RegExp(allianzVsnrPattern).test(normalizedVsnr) ? null : 'format';
}
